<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="$emit('close')"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">权限配置</h3>
            <p class="text-sm text-gray-500 mt-1">{{ account?.email || '' }}</p>
          </div>
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="flex items-center text-gray-500">
          <ArrowPathIcon class="animate-spin h-5 w-5 mr-2" />
          正在加载权限信息...
        </div>
      </div>

        <!-- 权限配置表单 -->
        <form v-else @submit.prevent="handleSubmit" class="space-y-6">
          <!-- 资源访问控制 -->
          <div class="space-y-6">
          
            <!-- 邮件账户 -->
            <div>
              <div class="flex items-center justify-between mb-3">
                <label class="text-sm font-medium text-gray-700">
                  可操作的邮件账户
                </label>
                <!-- 🔍 搜索框 -->
                <input
                  v-model="emailAccountSearchQuery"
                  type="text"
                  placeholder="搜索邮件账户..."
                  class="w-48 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <!-- 🔧 固定高度 + 滚动 -->
              <div v-if="availableResources.email_accounts?.length > 0" class="max-h-40 overflow-y-auto pr-2">
                <div v-if="filteredEmailAccounts.length > 0" class="flex flex-wrap gap-2">
                  <label v-for="account in filteredEmailAccounts" :key="account.id" class="cursor-pointer">
                    <input 
                      type="checkbox" 
                      :value="account.id"
                      v-model="permissions.accessible_email_accounts"
                      class="sr-only" 
                    />
                    <span 
                      class="inline-flex items-center px-3 py-2 text-sm font-medium transition-colors border"
                      :class="permissions.accessible_email_accounts.includes(account.id) 
                        ? 'bg-blue-100 text-blue-800 border-blue-300' 
                        : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200'"
                    >
                      <div class="text-left">
                        <div class="font-medium">{{ account.display_name || account.email }}</div>
                        <div class="text-xs opacity-75">{{ account.email }}</div>
                      </div>
                    </span>
                  </label>
                </div>
                <!-- 空状态 -->
                <div v-else class="text-center py-4 text-sm text-gray-400">
                  {{ emailAccountSearchQuery ? '未找到匹配的邮件账户' : '暂无可用邮件账户' }}
                </div>
              </div>
              <div v-else class="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                暂无可分配的邮件账户
              </div>
            </div>

            <!-- 客户分组 -->
            <div>
              <div class="flex items-center justify-between mb-3">
                <label class="text-sm font-medium text-gray-700">
                  可操作的客户分组
                </label>
                <!-- 🔍 搜索框 -->
                <input
                  v-model="customerGroupSearchQuery"
                  type="text"
                  placeholder="搜索客户分组..."
                  class="w-48 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <!-- 🔧 固定高度 + 滚动 -->
              <div v-if="availableResources.customer_groups?.length > 0" class="max-h-40 overflow-y-auto pr-2">
                <div v-if="filteredCustomerGroups.length > 0" class="flex flex-wrap gap-2">
                  <label v-for="group in filteredCustomerGroups" :key="group.id" class="cursor-pointer">
                    <input 
                      type="checkbox" 
                      :value="group.id"
                      v-model="permissions.accessible_customer_groups"
                      class="sr-only" 
                    />
                    <span 
                      class="inline-flex items-center px-3 py-2 text-sm font-medium transition-colors border"
                      :class="permissions.accessible_customer_groups.includes(group.id) 
                        ? 'bg-orange-100 text-orange-800 border-orange-300' 
                        : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200'"
                    >
                      <div class="text-left">
                        <div class="font-medium">{{ group.name }}</div>
                        <!--<div class="text-xs opacity-75">{{ group.customer_count || 0 }} 个客户</div>-->
                      </div>
                    </span>
                  </label>
                </div>
                <!-- 空状态 -->
                <div v-else class="text-center py-4 text-sm text-gray-400">
                  {{ customerGroupSearchQuery ? '未找到匹配的客户分组' : '暂无可用客户分组' }}
                </div>
              </div>
              <div v-else class="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                暂无可分配的客户分组
              </div>
            </div>

            <!-- 隐私设置 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-3">隐私设置</label>
              <div class="border border-gray-200 rounded-lg p-4">
                <label class="flex items-start">
                  <input
                    v-model="permissions.mask_customer_emails"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 focus:ring-0 border-gray-300 rounded mt-0.5"
                  />
                  <div class="ml-3">
                    <div class="text-sm font-medium text-gray-900">脱敏客户邮箱地址</div>
                    <div class="text-sm text-gray-600 mt-1">
                      开启后，子账户将无法看到完整的客户邮箱地址，邮箱将被部分遮挡显示（如：use***@example.com）
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <!-- 外部订单权限 -->
            <div>
              <button
                type="button"
                @click="showOrderPermissions = !showOrderPermissions"
                class="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors py-2"
              >
                <span>外部订单权限</span>
                <svg
                  :class="['h-5 w-5 transition-transform duration-200', showOrderPermissions ? 'rotate-180' : '']"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <div
                v-show="showOrderPermissions"
                class="space-y-3 mt-3 pl-1"
              >
                <div class="flex items-center">
                  <input
                    v-model="permissions.can_view_all_orders"
                    type="checkbox"
                    id="can_view_all_orders"
                    class="h-4 w-4 text-blue-600 focus:ring-0 border-gray-300 rounded"
                  />
                  <label for="can_view_all_orders" class="ml-2 text-sm text-gray-700">
                    查看全部订单
                    <span class="text-xs text-gray-500 ml-1">开启后可查看主账户下的所有订单，关闭则仅查看自己创建的订单</span>
                  </label>
                </div>
                
                <div class="flex items-center">
                  <input
                    v-model="permissions.can_delete_orders"
                    type="checkbox"
                    id="can_delete_orders"
                    class="h-4 w-4 text-blue-600 focus:ring-0 border-gray-300 rounded"
                  />
                  <label for="can_delete_orders" class="ml-2 text-sm text-gray-700">
                    删除外部订单
                  </label>
                </div>
                
                <div class="flex items-center">
                  <input
                    v-model="permissions.can_export_orders"
                    type="checkbox"
                    id="can_export_orders"
                    class="h-4 w-4 text-blue-600 focus:ring-0 border-gray-300 rounded"
                  />
                  <label for="can_export_orders" class="ml-2 text-sm text-gray-700">
                    导出外部订单
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- 错误信息 -->
          <div v-if="submitError" class="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-600">{{ submitError }}</p>
          </div>

          <!-- 按钮组 -->
          <div class="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="$emit('close')"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500/20 transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              :disabled="submitting"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span v-if="submitting">保存中...</span>
              <span v-else>保存配置</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { XMarkIcon, ArrowPathIcon } from '@heroicons/vue/24/outline'
import { subAccountService } from '@/services/subAccountService'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  account: {
    type: Object,
    default: null
  },
  availableResources: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close', 'updated'])

// 状态
const loading = ref(false)
const submitting = ref(false)
const submitError = ref('')
const showOrderPermissions = ref(false)  // 外部订单权限展开/折叠状态

// 🔍 搜索查询
const emailAccountSearchQuery = ref('')
const customerGroupSearchQuery = ref('')

// 权限数据
const permissions = reactive({
  // 资源访问控制
  accessible_email_accounts: [],
  accessible_customer_groups: [],
  
  // 外部订单权限
  can_view_all_orders: false,
  can_delete_orders: true,  // 默认允许删除
  can_export_orders: true,  // 默认允许导出
  
  // 隐私设置
  mask_customer_emails: true
})

// 🔧 过滤邮件账户
const filteredEmailAccounts = computed(() => {
  const accounts = props.availableResources.email_accounts || []
  if (!emailAccountSearchQuery.value.trim()) {
    return accounts
  }
  
  const query = emailAccountSearchQuery.value.toLowerCase()
  return accounts.filter(account => {
    const displayName = (account.display_name || '').toLowerCase()
    const email = (account.email || '').toLowerCase()
    return displayName.includes(query) || email.includes(query)
  })
})

// 🔧 过滤客户分组
const filteredCustomerGroups = computed(() => {
  const groups = props.availableResources.customer_groups || []
  if (!customerGroupSearchQuery.value.trim()) {
    return groups
  }
  
  const query = customerGroupSearchQuery.value.toLowerCase()
  return groups.filter(group => {
    const name = (group.name || '').toLowerCase()
    return name.includes(query)
  })
})

// 加载当前权限
const loadPermissions = async () => {
  if (!props.account?.id) {
    return
  }
  
  try {
    loading.value = true
    // 🔧 清空搜索框
    emailAccountSearchQuery.value = ''
    customerGroupSearchQuery.value = ''
    
    const response = await subAccountService.getSubAccountPermissions(props.account.id)
    
    // 更新权限数据
    Object.assign(permissions, {
      ...response,
      accessible_email_accounts: response.accessible_email_accounts_list || [],
      accessible_customer_groups: response.accessible_customer_groups_list || []
    })
    
  } catch (error) {
    console.error('加载权限失败:', error)
    submitError.value = '加载权限信息失败'
  } finally {
    loading.value = false
  }
}

// 提交权限更新
const handleSubmit = async () => {
  if (!props.account?.id) {
    return
  }
  
  try {
    submitting.value = true
    submitError.value = ''
    
    await subAccountService.updateSubAccountPermissions(props.account.id, permissions)
    emit('updated')
    
  } catch (error) {
    console.error('更新权限失败:', error)
    
    if (error.response?.data?.error) {
      submitError.value = error.response.data.error
    } else {
      submitError.value = '权限更新失败，请重试'
    }
  } finally {
    submitting.value = false
  }
}

// 监听模态框显示状态，当显示时加载权限
watch(
  () => props.show,
  (newShow) => {
    if (newShow && props.account?.id) {
      loadPermissions()
    } else if (!newShow) {
      // 关闭模态框时重置展开状态
      showOrderPermissions.value = false
    }
  },
  { immediate: true }
)
</script>
